'use client';

import { useUser } from '@clerk/nextjs';
import { errorTracker } from '@libs/logger';
import { useLocale } from 'next-intl';
import { useEffect, useRef } from 'react';

import { useEffectivePlan } from '../../hooks/use-effective-plan';
import { identifyUser } from '../../libs/observability/analytics.client';

/**
 * Fire-and-forget on landing for a signed-in user: (1) sync their GitHub org
 * memberships into Clerk orgs (POST /api/clerk/sync-orgs), and (2) capture the
 * active locale as their saved language if they don't have one yet (POST
 * /api/clerk/locale), so the proxy can auto-redirect them next time. Runs once
 * per mount; failures are swallowed so neither call blocks the page.
 *
 * It also stamps the Dash0 analytics identity once the effective plan resolves, so
 * every subsequent web signal (events, page views, RUM) carries `user.id` +
 * `app.plan` — the join key against backend spans.
 */
export function OrgSync(): null {
  const { isSignedIn, user } = useUser();
  const { plan, isLoaded } = useEffectivePlan();
  const locale = useLocale();
  const hasRun = useRef(false);
  const hasIdentified = useRef(false);

  useEffect(() => {
    if (isSignedIn !== true || hasRun.current) return;
    hasRun.current = true;

    // Identify the Dash0 RUM session + error reports by Clerk user id (no PII).
    errorTracker.setUser({ id: user.id });

    void fetch('/api/clerk/sync-orgs', { method: 'POST' }).catch(() => {
      // best-effort; org provisioning must not affect the page
    });

    const saved = (user.publicMetadata as { locale?: unknown }).locale;
    if (typeof saved !== 'string') {
      void fetch('/api/clerk/locale', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ locale }),
      }).catch(() => {
        // best-effort; language capture must not affect the page
      });
    }
  }, [isSignedIn, locale, user]);

  useEffect(() => {
    if (isSignedIn !== true || !isLoaded || hasIdentified.current) return;
    hasIdentified.current = true;
    identifyUser({ userId: user.id, plan, roles: [plan] });
  }, [isSignedIn, isLoaded, plan, user]);

  return null;
}
